import { useMutation } from '@src/query';


/**
 * Inventory Add/Edit Part form mutation hooks (T10). Each wraps the shared
 * `useMutation` engine with the injected-`queryClient`
 * `mutateFn({ apiClient, queryClient })` contract — the corelib query singleton
 * is NOT imported here. Invalidation runs inside `mutateFn` (the one place
 * `queryClient` is injected) so the exact parts cache key prefix is preserved.
 *
 * The parts list key prefix is built by `Inventory.jsx`
 * (`['root','','inventory','parts',sellerId]`) and threaded into the form as
 * `partsKeyPrefix`, so every hook here takes it as a mutate variable and
 * invalidates that exact key — identical to the original inline handlers.
 *
 * Mirrors `query/garage/useDonorMutations.js`: one
 * `query/<area>/use<Thing>Mutations.js` module re-exported through the
 * `@src/query` barrel.
 */

/** Cache key prefix for a seller's parts list, shared across Inventory queries. */
function partsKeyPrefix(sellerId) {
  return ['root', '', 'inventory', 'parts', String(sellerId || '')];
}


/**
 * Advisory "Find a part" catalog lookup (OE / part #). Variables:
 * `{ sellerId, code }`. Returns the lookup payload; the caller maps a match into
 * the form (prefill). No cache invalidation.
 */
function usePartLookupMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, code }) => apiClient.request({
      url: `/inventory/parts/${sellerId}/lookup/`,
      payload: { code: (code || '').trim() },
    }),
    ...params,
  });
}


/**
 * Save (create or update) a part, then best-effort upload staged photos and —
 * for a new part with a complete fitment — POST the base fitment. Variables:
 * `{ sellerId, partId, editing, payload, stagedPhotos, fitment, compressImage,
 * onUploadStart, onUploadDone, keyPrefix }`.
 *
 * Preserves the original orchestration EXACTLY:
 *  - PATCHes when `editing` OR `partId` is already set (a retry after a partial
 *    failure must PATCH the part it already created, never POST a duplicate);
 *    otherwise POSTs. Returns `{ saved, createdId, failedPhotos }` so the caller
 *    can stash the created id for retries and surface partial photo failures.
 *  - Photos post sequentially in staged order (index 0 lands first → backend
 *    auto-marks it primary) via `compressImage` + multipart; a failed photo is
 *    collected, never treated as "part not saved". `onUploadStart(url)` /
 *    `onUploadDone(url)` drive the per-tile spinner + footer counter.
 *  - For a NEW part with year+make+model, POSTs the fitment; a benign duplicate
 *    (donor-derived server-side) is swallowed.
 *  - Invalidates `keyPrefix` (the parts list) on success.
 */
function usePartSaveMutation(params) {
  return useMutation({
    mutateFn: async ({
      apiClient, queryClient, sellerId, partId, editing, payload,
      stagedPhotos = [], fitment, compressImage, onUploadStart, onUploadDone, keyPrefix,
    }) => {
      // A retry after a partial failure must PATCH the part it already created,
      // never POST a duplicate (review fix).
      const saved = editing || partId
        ? await apiClient.request({
          url: `/inventory/parts/${sellerId}/${partId}/`,
          method: 'PATCH',
          payload,
        })
        : await apiClient.request({
          url: `/inventory/parts/${sellerId}/`,
          method: 'POST',
          payload,
        });
      const createdId = editing ? null : saved.id;

      // Upload photos best-effort: the part is a valid (partial) record either
      // way — a failed photo must not read as "the part wasn't saved". They post
      // sequentially in staged order so index 0 (the chosen primary) lands first
      // and the backend auto-marks it primary. A per-tile spinner tracks progress.
      const failedPhotos = [];
      for (const photo of stagedPhotos) {
        onUploadStart?.(photo.url);
        try {
          const compressed = await compressImage(photo.file);
          const body = new FormData();
          body.append('image', compressed);
          await apiClient.request({
            url: `/inventory/parts/${sellerId}/${saved.id}/photos/`,
            method: 'POST',
            payload: body,
            contentType: 'multipart/form-data',
          });
        }
        catch {
          failedPhotos.push(photo);
        }
        finally {
          onUploadDone?.(photo.url);
        }
      }

      if (!editing && fitment?.year && fitment?.make && fitment?.model) {
        try {
          await apiClient.request({
            url: `/inventory/parts/${sellerId}/${saved.id}/fitments/`,
            method: 'POST',
            payload: fitment,
          });
        }
        catch {
          // Benign duplicate: a linked donor already derived this exact
          // fitment server-side (review fix) — the part is saved either way.
        }
      }

      queryClient.invalidateQueries({ queryKey: keyPrefix });
      return { saved, createdId, failedPhotos };
    },
    ...params,
  });
}


/**
 * Search-first intake (#263) over the #262 batch endpoint: create prefilled
 * DRAFT parts from part-type ids (single select and the visual picker's batch
 * are the same call). Variables:
 * `{ sellerId, partTypeIds, donorVehicleId, keyPrefix }`. Invalidates the
 * parts list; resolves the endpoint payload `{ parts: [...] }`.
 */
function usePartsIntakeMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, partTypeIds, donorVehicleId, keyPrefix }) => {
      const created = await apiClient.request({
        url: `/inventory/parts/${sellerId}/intake/`,
        method: 'POST',
        payload: { part_type_ids: partTypeIds, donor_vehicle_id: donorVehicleId ?? null },
      });
      queryClient.invalidateQueries({ queryKey: keyPrefix });
      return created;
    },
    ...params,
  });
}


/**
 * Request a new part type (sellers never mutate the taxonomy directly).
 * Variables: `{ sellerId, name }`. POSTs the proposed name. No cache
 * invalidation (the request is reviewed off-band).
 */
function usePartTypeRequestMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, name }) => apiClient.request({
      url: `/inventory/parts/${sellerId}/part-type-requests/`,
      method: 'POST',
      payload: { proposed_name: (name || '').trim() },
    }),
    ...params,
  });
}


/**
 * Saved-photo actions (edit mode only): set-primary / reorder / delete. The
 * caller passes a `run({ apiClient })` thunk for the specific endpoint; this hook
 * runs it then invalidates the parts list. Variables: `{ run, keyPrefix }`.
 * Mirrors the original inline `savedPhotoMutation` (refetch is the caller's
 * `onSuccess`).
 */
function usePartPhotoMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, run, keyPrefix }) => run({ apiClient }).then(() => {
      queryClient.invalidateQueries({ queryKey: keyPrefix });
    }),
    ...params,
  });
}


export {
  partsKeyPrefix,
  usePartLookupMutation,
  usePartPhotoMutation,
  usePartSaveMutation,
  usePartsIntakeMutation,
  usePartTypeRequestMutation,
};
